<template>
  <div class="banner-module">
    <div
      v-if="showNextTemplateButton || selectedInstanceIndex"
      class="d-flex justify-content-between mb-3"
    >
      <b-button-group size="sm">
        <b-button
          v-if="showNextTemplateButton"
          variant="outline-primary"
          @click="moveInstanceSelection(1)"
        >
          <b-icon icon="arrow-down" />
          Next template
        </b-button>
        <b-button
          v-if="selectedInstanceIndex"
          variant="outline-primary"
          @click="moveInstanceSelection(-1)"
        >
          Prev template
          <b-icon icon="arrow-up" />
        </b-button>
      </b-button-group>
      <span>{{ totalTemplateOrder }}/{{ totalTemplatesCount }}</span>
    </div>
    <b-tabs
      content-class="mt-3"
      pills
      justified
      small
    >
      <b-tab title="Content">
        <div
          v-show="canAddText()"
          class="layout-sidebar-section"
        >
          <div
            class="layout-sidebar-section-title"
            @click="$emit('toggleSection', 'textEditorFieldsSection')"
          >
            <div class="d-flex align-items-center">
              <i class="bi bi-fonts green-fonts" />
              <strong> Text</strong>
            </div>
            <b-icon
              :icon="
                isOpen.textEditorFieldsSection ? 'chevron-up' : 'chevron-down'
              "
            />
          </div>
          <transition 
            class="border-bottom"
          >
            <b-collapse
              :visible="isOpen.textEditorFieldsSection"
            >
              <div
                :key="'text-section-' + editModeKey"
                class="border-bottom pb-2 mb-2"
              >
                <b-form-group
                  v-for="(field, index) in textFields"
                  :key="`${field.element_id}_${index}`"
                  :label-for="`field_${field.id}`"
                >
                  <div class="text-actions d-flex justify-content-between align-items-center mb-1">
                    <span class="font-medium text-xs">{{ field.name }}</span>
                    <i
                      v-b-tooltip.hover.top="'Delete Text'"
                      class="bi bi-x float-end cursor-pointer"
                      @click="handleDeleteText(field)"
                    />
                  </div>
                  <component
                    :is="field.type === 'Multi Line Text' && (selectedInstance?.data?.[field.element_id] || '').includes('\n') ? 'b-textarea' : 'b-input'"
                    :id="`field_${field.element_id}`"
                    :value="selectedInstance.data[field.element_id]"
                    data-ai-agent-type="input[type=text]"
                    :data-ai-agent-id="`text-layer-input-${field.element_id}`"
                    data-ai-agent-label="Text Layer Input"
                    data-ai-agent-role="dynamic-text-input"
                    @focus="setActiveObject(field)"
                    @input="updateField(field, $event)"
                    @click.stop
                    @blur="onBlurText"
                  />
                </b-form-group>
                <b-form-group v-if="canAddText()">
                  <button
                    class="btn custom-secondary-btn w-100 text-xs font-medium"
                    data-ai-agent-type="button"
                    data-ai-agent-id="add-text-btn"
                    data-ai-agent-label="Add Text"
                    @click="addNewTextField"
                  >
                    <i class="bi bi-plus-lg" /> Add Text
                  </button>
                </b-form-group>
              </div>
            </b-collapse>
          </transition>
        </div>
        <div
          v-show="canAddDecorativeImages()"
          class="layout-sidebar-section"
        >
          <div
            class="layout-sidebar-section-title"
            @click="$emit('toggleSection', 'backgroundFieldsSection')"
          >
            <div class="d-flex align-items-center">
              <i class="bi bi-palette green-fonts" />
              <strong> Background</strong>
            </div>
            <b-icon
              :icon="
                isOpen.backgroundFieldsSection ? 'chevron-up' : 'chevron-down'
              "
            />
          </div>
          <transition class="border-bottom">
            <b-collapse :visible="isOpen.backgroundFieldsSection">
              <div
                :key="'background-section-' + editModeKey"
                class="border-bottom pb-2 mb-2"
              >
                <b-form-group>
                  <div class="d-flex justify-content-between align-items-center flex-column">
                    <div class="colors-item d-flex justify-content-between align-items-center">
                      <label class="mb-1">
                        <strong style="color: #209c86">Color</strong>
                      </label>
                      <div
                        v-for="(color, index) in editor?.colors"
                        :key="index"
                        class="d-flex align-items-center"
                      >
                        <b-input
                          v-model="editor.colors[index]"
                          class="color-fill-item w-10 no-right-border custom-color-picker"
                          debounce="50"
                          type="color"
                          @update="redrawBackground"
                        />
                        <b-input
                          v-model="editor.colors[index]"
                          style="height: 38px;"
                          class="w-100 no-left-border"
                          type="text"
                          @change="onInputColor($event, index)"
                        />
                      </div>
                    </div>
                    <div
                      class="d-flex flex-row align-items-center mb-1 position-relative w-100"
                      style="margin-top: 10px;"
                    >
                      <label
                        class="mb-0 mr-2"
                        style="min-width: 130px;"
                      >
                        <strong style="color: #209c86">Image</strong>
                      </label>

                      <div
                        class="d-flex flex-row justify-content-between flex-grow-1 image-multiselect"
                        :class="{ 'drag-over': isDragOver }"
                        @dragenter.prevent="onDragEnter($event)"
                        @dragover.prevent="onDragOver($event)"
                        @drop.prevent="onDrop($event)"
                      >
                        <Multiselect
                          v-model="selectedBackground"
                          class="background-dropdown flex-grow-1"
                          track-by="id"
                          label="name"
                          placeholder=""
                          :options="getBackgroundFields"
                          @close="onBlurText"
                          @open="getFields"
                        >
                          <template #singleLabel="props">
                            <div
                              class="background-dropdown-label"
                              @mouseenter="hoveredTag = true"
                              @mouseleave="hoveredTag = null"
                            >
                              <div class="background-dropdown-label__image">
                                <img
                                  :src="props.option.thumbnail"
                                  :alt="props.option.name"
                                  class="preview-img"
                                  @click.stop.prevent
                                >
                              </div>
                            </div>
                          </template>
                          <template #option="props">
                            <div class="background-dropdown-option">
                              <div class="background-dropdown-option__image">
                                <img
                                  :src="props.option.thumbnail"
                                  :alt="props.option.name"
                                >
                              </div>
                              <p class="background-dropdown-option__text">
                                {{ props.option.name }}
                              </p>
                            </div>
                          </template>
                          <template #caret>
                            <span class="custom-arrow">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#333"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </span>
                          </template>
                        </Multiselect>
                        <b-button
                          variant="outline-primary"
                          class="upload-button d-flex align-items-center justify-content-center"
                          @click.stop.prevent="onChange('selectBackground', 'Replace')"
                        >
                          <b-icon icon="upload" />
                          <span style="margin-left: 10px;" />
                        </b-button>
                      </div>
                    </div>
                  </div>
                </b-form-group>
              </div>
            </b-collapse>
          </transition>
        </div>
        <div
          v-show="canAddDecorativeImages()"
          class="layout-sidebar-section"
        >
          <div
            class="layout-sidebar-section-title"
            @click="$emit('toggleSection', 'aiGenerateFieldsSection')"
          >
            <div class="d-flex align-items-center">
              <i class="bi bi-stars green-fonts" />
              <strong> AI Generate</strong>
            </div>
            <b-icon
              :icon="
                isOpen.aiGenerateFieldsSection ? 'chevron-up' : 'chevron-down'
              "
            />
          </div>
          <transition class="border-bottom">
            <b-collapse :visible="isOpen.aiGenerateFieldsSection">
              <div class="border-bottom pb-2 mb-2 scroll-images-section">
                <div 
                  class="d-flex flex-wrap align-items-center"
                >
                  <button
                    class="btn btn-primary generate-background custom-gen"
                  > 
                    <i class="bi bi-stars" /> 
                    <span class="ml-2">Generate Image</span>
                  </button>
                </div>
              </div>
            </b-collapse>
          </transition>
        </div>
        <div 
          class="layout-sidebar-section"
        >
          <div
            class="layout-sidebar-section-title"
            @click="$emit('toggleSection', 'productImagesFieldsSection')"
          >
            <div class="d-flex align-items-center">
              <i class="bi bi-file-earmark-image green-fonts" />
              <strong> Product Images</strong>
            </div>
            <b-icon
              :icon="
                isOpen.productImagesFieldsSection ? 'chevron-up' : 'chevron-down'
              "
            />
          </div>
          <transition class="border-bottom">
            <b-collapse :visible="isOpen.productImagesFieldsSection">
              <div class="border-bottom pb-2 mb-2 scroll-images-section">
                <div 
                  class="d-flex flex-wrap align-items-center"
                  :class="{ 'drag-over': isDragOverProductImages }"
                  @dragenter.prevent="onDragEnterProductImages"
                  @dragover.prevent="onDragOverProductImages"
                  @dragleave.prevent="onDragLeaveProductImages"
                  @drop.prevent="onDropProductImage"
                >
                  <div
                    v-for="image in getProductImages"
                    :key="image.id"
                    class="label-option general-card-template product-card-template"
                    :class="{ selected: activeTarget?.id === image.id }"
                    @drop.stop.prevent="(e) => onDropReplaceProductImage(e, image)"
                    @dragover.prevent
                  >
                    <button 
                      class="close-btn"
                      @click.stop="handleProductDelete(image)" 
                    >
                      <i class="bi bi-trash3" />
                    </button>
                    <button
                      v-b-tooltip.hover.viewport.noninteractive="'Replace image'"
                      class="replace-image-btn"
                      @click="toggleProductDropdown(image, 'replace')"
                    >
                      <i class="bi bi-repeat" />
                    </button>
                    <img 
                      class="image-option"
                      :src="image.thumbnail" 
                      :alt="image.name"
                      @click.stop="setActiveObject(image)"
                    >
                  </div>
                  <div 
                    style="width: 60px;height: 60px;"
                    class="d-flex align-items-center justify-content-center additional-card-template"
                    @click="toggleProductDropdown(null, 'add')"
                  >
                    <i 
                      v-b-tooltip.hover.top
                      title="Add product images"
                      class="fas fa-plus-circle add-icon" 
                    />
                  </div>
                </div>
              </div>
            </b-collapse>
          </transition>
        </div>
        <div
          v-show="canAddDecorativeImages()"
          class="layout-sidebar-section"
        >
          <div
            class="layout-sidebar-section-title"
            @click="$emit('toggleSection', 'imagesFieldSection')"
          >
            <div class="d-flex align-items-center">
              <i class="bi bi-card-image green-fonts" />
              <strong>Images</strong>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                v-if="hasUnclassifiedImages"
                size="sm"
                variant="outline-primary"
                class="mr-2 detect-text-btn"
                :disabled="isClassifyingTextImages"
                @click.stop="handleDetectTextImages"
              >
                <b-spinner
                  v-if="isClassifyingTextImages"
                  small
                />
                <i
                  v-else
                  class="bi bi-fonts"
                />
                <span class="ml-1">Detect Text</span>
              </b-button>
              <b-icon
                :icon="
                  isOpen.imagesFieldSection ? 'chevron-up' : 'chevron-down'
                "
              />
            </div>
          </div>
          <transition class="border-bottom">
            <b-collapse :visible="isOpen.imagesFieldSection">
              <div
                class="border-bottom pb-2 mb-2 scroll-images-section"
                @drop.prevent="onDropAddImage"
                @dragover.prevent
              >
                <div
                  :key="'images-section-' + editModeKey"
                  class="d-flex flex-wrap align-items-center"
                >
                  <div
                    v-for="image in getImages"
                    :key="image.id"
                    class="label-option general-card-template image-card-template"
                    :class="{ selected: activeTarget?.id === image.id }"
                    @drop.stop.prevent="(e) => canEditImage(image) && onDropReplaceLayoutImage(e, image)"
                    @dragover.prevent
                  >
                    <button
                      v-if="canEditImage(image)"
                      class="close-btn"
                      @click.stop="handleImageDelete(image)"
                    >
                      <i class="bi bi-trash3" />
                    </button>
                    <button
                      v-if="canEditImage(image)"
                      v-b-tooltip.hover.viewport.noninteractive="'Replace image'"
                      class="replace-image-btn"
                      @click="replaceImage(image, 'selectBackground', 'Replace')"
                    >
                      <i class="bi bi-repeat" />
                    </button>
                    <button
                      v-if="isTextOnlyImage(image)"
                      v-b-tooltip.hover.viewport.noninteractive="'Convert to editable text'"
                      class="convert-text-btn"
                      :disabled="convertingImageId === image.id"
                      @click.stop="handleConvertToText(image)"
                    >
                      <b-spinner
                        v-if="convertingImageId === image.id"
                        small
                      />
                      <i
                        v-else
                        class="bi bi-fonts"
                      />
                    </button>
                    <div
                      v-if="!loaded"
                      class="image-loader"
                    >
                      <b-spinner small />
                    </div>
                    <img
                      class="image-option"
                      :class="{ 'is-loading': !loaded }"
                      :src="image.thumbnail"
                      :alt="image.name"
                      @load="onImageLoad"
                      @error="onImageLoad"
                      @click.stop="canEditImage(image) && setActiveObject(image)"
                    >
                  </div>
                  <div
                    v-if="canAddDecorativeImages()"
                    class="d-flex align-items-center justify-content-center additional-card-template"
                    @click="addAdditionalField('Background Theme Image')"
                  >
                    <i
                      v-b-tooltip.hover.top
                      title="Add images"
                      class="fas fa-plus-circle add-icon"
                    />
                  </div>
                </div>
              </div>
            </b-collapse>
          </transition>
        </div>
        <div class="sticky-dropdown">
          <span>Apply to:</span>
          <SelectTemplateDropdown
            class="d-inline"
            :local-storage-key="LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT"
            :clear-local-storage="false"
            :show-apply-button="true"
            @apply="confirmApply"
          />
        </div>
        <ProductImageDropdown
          ref="productImageDropdownFields"
          v-model="fileNameIds"
          :is-tab-active="false"
          :is-file-image="options.isFileImage"
          :is-fields-tab="true"
          @input="onChange('productImage', fileNameIds)"
          @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
        />
      </b-tab>
      <b-tab
        v-if="canAddText()"
        title="Style"
      >
        <div class="mb-3">
          <label>
            <strong>Select Field</strong>
          </label>
          <div>
            <Multiselect
              v-model="selected"
              :options="textFields"
              :multiple="true"
              track-by="element_id"
              label="name"
              placeholder="Select..."
              :close-on-select="false"
            />
          </div>
        </div>
        <div
          v-if="(isTextField.length > 0 || selected.length > 0)"
        >
          <div 
            v-if="options.colorSelector"
            class="d-flex align-items-center justify-content-between w-100 color-checkbox-container mb-3"
          >
            <label
              class="mb-0 mr-2"
            >
              <strong>Color</strong>
            </label>
            <ColorPicker
              :value="currentTarget?.fontColor"
              :themes="themes"
              position="top-start"
              @input="onTextChange('fontColor', $event)"
            />
          </div>
          <b-form-row class="mb-3">
            <b-col>
              <b-form-group
                :disabled="target.locked"
              >
                <template #label>
                  <strong>Font Size</strong>
                </template>
                <b-form-input
                  :value="config.fontSize"
                  type="number"
                  min="0"
                  @input="onTextChange('fontSize', $event)"
                  @keypress="preventNegativeInput"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-group>
            <template #label>
              <div class="d-flex align-items-center justify-content-between">
                <strong>Alignment</strong>
              </div>
            </template>
            <div class="d-flex gap-2 mb-3">
              <b-button 
                :variant="(currentTarget?.textAlign) === 'left,center' ? 'primary' : 'light'"
                class="flex-grow-1" 
                @click="onTextChange('textAlignment', `left,center`)"
              >
                <i class="bi bi-text-left" />
              </b-button>
              <b-button 
                :variant="(currentTarget?.textAlign) === 'center,center' ? 'primary' : 'light'" 
                class="flex-grow-1" 
                @click="onTextChange('textAlignment', `center,center`)"
              >
                <i class="bi bi-text-center" />
              </b-button>
              <b-button 
                :variant="(currentTarget?.textAlign) === 'right,center' ? 'primary' : 'light'" 
                class="flex-grow-1" 
                @click="onTextChange('textAlignment', `right,center`)"
              >
                <i class="bi bi-text-right" />
              </b-button>
            </div>
          </b-form-group>
          <div>
            <div class="d-flex align-items-center justify-content-between pb-2">
              <strong>Text Spacing</strong>
            </div>
            
            <b-input-group class="mb-3">
              <b-input-group-prepend class="pointer">
                <b-input-group-text @click="decrease">
                  <i class="glyphicon glyphicon-minus" />
                </b-input-group-text>
              </b-input-group-prepend>

              <b-form-input
                :value="currentTarget?.charSpacing"
                type="number"
                :step="1"
                style="width: 94px;"
                @input="onTextChange('textSpacing', $event)"
              />

              <b-input-group-append class="pointer">
                <b-input-group-text @click="increase">
                  <i class="glyphicon glyphicon-plus" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
          </div>
          <div>
            <div class="d-flex align-items-center justify-content-between mb-2 custom-shadow">
              <strong>Shadows</strong>
              <b-form-checkbox
                id="shadows-checkbox"
                v-model="isShadowEnabled"
                :unchecked-value="false"
                switch
                size="lg"
                @change="$refs.textShadows.setEnabled($event)"
              />
            </div>
            <transition>
              <b-collapse :visible="isShadowEnabled">
                <ShadowsModule 
                  ref="textShadows"
                  :target="currentTarget" 
                  :is-banner-builder="true"
                  :targets="targets"
                />
              </b-collapse>
            </transition>
          </div>
          <ApplyToAllTextDropdown
            class="sticky-dropdown"
            :options="options"
            :target="currentTarget"
            :is-banner-builder="true"
          />
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import { setSelectedInstance } from '@frontend/group/modules/copy-paste-object'
import {GLOBAL_UPDATE_LOCAL_STORAGE_KEY, isGlobalUpdateOn} from "@frontend/constants/global-update";
import FormGroupCharLimit from "@frontend/components/common/atoms/FormGroupCharLimit.vue";
import { LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
  LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
  LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE
 } from "@frontend/group/modules/editor-control-popup/selected-template-for-apply-to-all";
 import {deepCopy} from '@frontend/utils';
 import { FIELD_TYPE_BACKGROND_THEME_IMAGE, FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
 import Multiselect from 'vue-multiselect';
 import { FIELD_NAME_IMAGE_AREA } from '@frontend/constants/name-fields-of-template';
 import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields'
import { getObjectsForOrder } from '@frontend/group/modules/copy-and-resize';
import { 
  IMAGE_PREFIX_ID, 
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID, 
  TEXT_PREFIX_ID, 
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  UPLOAD_IMAGE_PREFIX_ID,
  IMAGE_FROM_BK_PREFIX_ID,
  KEY_GROUP
 } from '@frontend/constants/templates-field-prefixes';
import ProductImageDropdown from '@frontend/components/modules/layouts-page/EditorControl/components/ProductImageDropdown.vue';
import SelectTemplateDropdown from "@frontend/components/modules/layouts-page/EditorControl/components/SelectTemplateDropdown.vue";
import {
  uploadProductImageAndDraw,
  validateProductImage
} from "@frontend/group/modules/add-product-image/add-product-image-service";
import TemplateDragDropService from "@frontend/group/modules/template-events/template-drag-and-drop-service";
import ColorPicker from '@frontend/components/modules/layouts-page/EditorControl/components/ColorPicker.vue'
import ShadowsModule from '@frontend/components/modules/layouts-page/EditorControl/ShadowsModule.vue';
import ApplyToAllTextDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllTextDropdown.vue';
import {FIELD_TYPE_MULTI_LINE_TEXT,} from '@frontend/constants/type-fields-of-template'
import { editModeService } from '@frontend/services/EditModeService'
import { editModeManager } from '@frontend/services/EditModeManager'
import { EDIT_MODE } from '@frontend/constants/edit-mode'

const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'layoutChanges',
  'getChangesLayoutById',
  'saveLayout',
  'updateLayoutChanges',
  'handleObjectMoving',
  'onClickDuplicateTemplateButton',
]);

const BG_THEME_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
];

const IMG_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  UPLOAD_IMAGE_PREFIX_ID,
  IMAGE_FROM_BK_PREFIX_ID,
];

export default {
  name: 'BannerBuilderEditor',
  components: {
    FormGroupCharLimit,
    Multiselect,
    ProductImageDropdown,
    SelectTemplateDropdown,
    ColorPicker,
    ShadowsModule,
    ApplyToAllTextDropdown,
  },
  props: {
    isOpen: {
      type: Object,
      default: () => ({
        textEditorFieldsSection: true,
        backgroundFieldsSection: true,
        productImagesFieldsSection: true,
        imagesFieldSection: true,
        aiGenerateFieldsSection: false,
      }),
    },
    options: {
      type: Object,
      default: () => {},
    },
    target: {
      type: Object,
      default: () => null,
      required: false,
    },
    defaultColor: {
      type: String,
      default: "#ffffff",
      required: false
    },
    themes: {
      type: Array,
      default: () => [],
    },
    activeTarget: {
      type: Object,
      default: () => null,
      required: false,
    },
    typeEditorControl: {
      type: String,
      required: false,
      default: 'TEXT'
    }
  },
  data: () => ({
    isApplyToAllGlobalUpdate: false,
    editor: {},
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
    backgroundProperties: {
      selectedField: null,
    },
    fields: [],
    hoveredTag: null,
    isShowDropdown: false,
    isContracted: false,
    fileNameIds: '',
    loaded: false,
    imagesLoadedCount: 0,
    isDragOverProductImages: false,
    isDragOver: false,
    config: { fontSize: 0 },
    isShadowEnabled: false,
    selected: [],
    currentTarget: {},
    targets: [],
    temporaryTarget: null,
    isEditRestricted: false,
    editModeKey: 0,
    currentEditMode: null,
    // Convert Image to Text feature
    textImagesClassified: false,
    isClassifyingTextImages: false,
    textOnlyImageIds: new Set(),
    classifiedImageIds: new Set(), // All classified images (text + image types)
    convertingImageId: null,
  }),

  computed: {
    ...mapGetters('layout', [
      'layers',
      'allProductImages',
      'selectedInstance',
      'selectedInstanceIndex',
      'selectedInstanceFieldsWithType',
      'selectedInstanceAdditionalFields',
      'selectedInstanceTextBoxFields',
      'currentPagePreviews',
    ]),
    textFields () {
      return this.selectedInstanceTextBoxFields.filter(field => field.visible === true).reverse();
    },
    multilineTextFields () {
      return this.selectedInstanceFieldsWithType('Multi Line Text')
    },
    isMultiLineField () {
      return [FIELD_TYPE_MULTI_LINE_TEXT].includes(this.currentTarget?.fieldType ?? this.currentTarget?.templateField?.type);
    },
    isTextField () {
      return this.selectedInstanceTextBoxFields?.filter(field => field.element_id === this.currentTarget?.element_id);
    },
    additionalTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Text');
    },
    additionalMultilineTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Multi Line Text');
    },
    showNextTemplateButton () {
      return !!this.currentPagePreviews.at(this.selectedInstanceIndex + 1)
    },
    currentPage() {
      return +$('input[name="current_page"]').val()
    },
    perPage() {
      return +$('input[name="per_page"]').val()
    },
    totalTemplateOrder () {
      return this.selectedInstanceIndex + 1 + (this.currentPage - 1) * this.perPage
    },
    totalTemplatesCount () {
      return window.preview_data.searchTemplateDropdownItems.length
    },
    getBackgroundFields() {
      return this.fields.filter(field => BG_THEME_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible);
    },
    getProductImages() {
      this.getFields();

      const objectsFiltered = [...this.selectedInstance.canvas.getObjects()].reverse();
      //order product images based on layers
      const filteredFields = this.fields.filter(field => field?.id?.startsWith(IMAGE_PREFIX_ID) && field.visible);

      if (objectsFiltered?.length > 0) {
        return _.sortBy(filteredFields, field => objectsFiltered.findIndex(object => object.fieldId === field.templateFieldId));
      }

      return filteredFields;
    },
    getImages() {
      return this.getImagesFiltered();
    },
    hasUnclassifiedImages() {
      const images = this.getImages;
      if (images.length === 0) return false;

      // Only check Background Theme Images (bk_theme_img_ and add_bk_theme_img_)
      // These are the only types that get classified during PSD import
      const classifiableImages = images.filter(img =>
        BG_THEME_PREFIXES.some(prefix => img.id?.startsWith(prefix))
      );

      if (classifiableImages.length === 0) return false;

      // Check if any classifiable image is not in the classified set
      for (const image of classifiableImages) {
        if (!this.classifiedImageIds.has(image.id)) {
          return true;
        }
      }
      return false;
    },
    isBannerBuilder() {
      return window.isBannerBuilder;
    },
    selectedBackground: {
      get() {
        const canvas = this.selectedInstance?.canvas;
        const objects = canvas.getObjects() || [];
        return this.fields.find(option => option?.id === this.backgroundProperties.selectedField) || 
        this.getBackgroundFields.find(option => option?.name.toLowerCase().includes('background')) ||
        this.getBackgroundFields.find(option => objects.some(f => f.id === option.id && canvas.width <= f.width * f.scaleX && canvas.height <= f.height * f.scaleY)) ||
        null;
      },
      set(option) {
        this.backgroundProperties.selectedField = option ? option?.id : null;
      }
    },
    isDisplayedProductImagesSection() {
      return this.getProductImages.length === 0 && this.allProductImages?.length;
    },
  },
  watch: {
    getBackgroundFields(newOptions) {
      newOptions.map(item => {
        if(item.name === "Background"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Background Image"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Image Area"){
          this.backgroundProperties.selectedField = item.id;
        }
      });
    },
    target() {
      this.initEditor();
    },
    activeTarget() {
      this.currentTarget = this.activeTarget;
      this.config.fontSize = this.currentTarget?.fontSize;
      this.isShadowEnabled = this.currentTarget?.shadow ? true : false;

      if (!this.currentTarget) {
        this.selected = [];
        return;
      }

      this.handleSectionsState();
      this.$nextTick(() => {
        const el = this.$el.querySelector('.selected')
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      let selectedField = this.textFields.find(field => field.element_id === this.currentTarget?.element_id);

      if (!selectedField) return;

      if (!this.currentTarget?.element_id) {
        selectedField = this.textFields.find(field => 
          this.currentTarget._objects?.some(obj => obj.element_id === field.element_id)
        );
      }
      this.selected = selectedField;
    },
    temporaryTarget() {
      this.currentTarget = this.temporaryTarget;
      this.config.fontSize = this.currentTarget?.fontSize;
      this.isShadowEnabled = this.currentTarget?.shadow ? true : false;
    },
    selected() {
      const canvas = this.selectedInstance?.canvas;
      if (!this.selected) return;

      if (!this.currentTarget || this.selected.length === 1) {
        // get only the first selected object
        const selectedObjects = canvas?.getObjects()?.filter(
          obj => this.selected[0]?.element_id === obj.element_id
        );

        // keep it in temporaryTarget & override currentTarget
        this.temporaryTarget = selectedObjects[0];
      }

      if (!Array.isArray(this.selected)) {
        this.targets = [];
        const selectedObjects = canvas?.getObjects()?.filter(
          obj => this.selected?.element_id === obj.element_id
        );
        this.currentTarget = selectedObjects[0];
        canvas.discardActiveObject()
        editorControlEventBroker.fire(EVENTS.SET_ACTIVE_OBJECT, { target: this.currentTarget })
        canvas.requestRenderAll()
      } else {
        const selectedObjects = canvas?.getObjects()?.filter(
          obj => this.selected.some(field => field.element_id === obj.element_id)
        );
        this.targets = selectedObjects;
        
        selectedObjects.forEach(obj => {
              if (obj.setCoords) {
                obj.setCoords();
              }
        });
        this.selectedInstance?.canvas.discardActiveObject();

        // create a new active selection
        const selection = new fabric.ActiveSelection(selectedObjects, {
          canvas: this.selectedInstance?.canvas
        })

        // set it as active
        selection.setCoords();
        this.selectedInstance?.canvas.setActiveObject(selection)
        this.selectedInstance?.canvas.requestRenderAll()
        this.$store.commit('layout/SET_TARGET', selection);
      }

      this.$emit('update')
    },
    selectedInstance() {
      this.selected = [];
      this.targets = [];
      this.currentTarget = {};
    },
    typeEditorControl() {
      if (this.typeEditorControl === 'GROUP') {
        const selectedFields = this.textFields?.filter(
        obj => this.selectedInstance?.canvas?.getActiveObjects()?.some(field => field.element_id === obj.element_id));

        this.selected = selectedFields;
      }
    }
  },
  mounted() {
    this.currentTarget = this.activeTarget;
    this.isApplyToAllGlobalUpdate = isGlobalUpdateOn();
    if (localStorage.getItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY) === null) {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);
    }
    editorControlEventBroker.on(EVENTS.TOGGLE_LOADING, this.toggleLoading.bind(this));
    editorControlEventBroker.on(EVENTS.GLOBAL_UPDATE, () => {
      this.isApplyToAllGlobalUpdate = !this.isApplyToAllGlobalUpdate;
      this.onChangeApplyToAllGlobal();
    });
    this.initEditor();
    this.getFields();
    this.initTextImageClassifications();
    this.removeConvertedImages();
    this.config = {
      fontSize: this.currentTarget?.fontSize,
    };

    this.isShadowEnabled = this.currentTarget?.shadow ? true : false;

    // Listen for edit mode changes to update sidebar restrictions
    this.isEditRestricted = editModeService.isRestricted();
    this.currentEditMode = editModeService.getEditMode();
    editModeManager.onModeChange(() => {
      this.isEditRestricted = editModeService.isRestricted();
      this.currentEditMode = editModeService.getEditMode();
      this.editModeKey++; // Force re-evaluation of canEditImage
    });
  },

  methods: {
    ...mapActions('fileBrowserProductSelector', [
      'disableMoveProductsMode',
      'enableReplaceMode',
      'setSelectedCanvasElement',
    ]),
    /**
     * Check if an image element can be edited based on current edit mode
     * @param {Object} image - The image field object from the sidebar
     * @returns {boolean} - True if the image can be edited
     */
    canEditImage(image) {
      if (!image) return false;

      // Get the canvas object for this image
      const canvasObject = this.selectedInstance?.canvas?.getObjects()?.find(
        obj => obj.id === image.id || obj.element_id === image.id
      );

      if (!canvasObject) return true; // If no canvas object found, allow edit

      return editModeService.canEditElement(canvasObject);
    },
    /**
     * Check if edit mode has restrictions active
     * Uses reactive property updated via editModeManager callback
     * @returns {boolean}
     */
    isEditModeRestricted() {
      return this.isEditRestricted;
    },
    /**
     * Check if decorative images can be added in current edit mode
     * Allowed in FULL_EDIT and TEXT_PRODUCT_IMAGE modes
     * Uses reactive currentEditMode property
     * @returns {boolean}
     */
    canAddDecorativeImages() {
      // Default to true if mode not yet set (behave as FULL_EDIT)
      if (!this.currentEditMode) return true;
      return this.currentEditMode === EDIT_MODE.FULL_EDIT || this.currentEditMode === EDIT_MODE.TEXT_PRODUCT_IMAGE;
    },
    /**
     * Check if text can be added in current edit mode
     * Allowed in all modes except PRODUCT_ONLY
     * @returns {boolean}
     */
    canAddText() {
      // Default to true if mode not yet set (behave as FULL_EDIT)
      if (!this.currentEditMode) return true;
      return this.currentEditMode !== EDIT_MODE.PRODUCT_ONLY;
    },
    initEditor() {
      this.editor = this.target?.editor;
      if (this.editor?.colors?.length === 0) {
        this.editor.colors = [this.defaultColor];
      }
    },
    getFields() {
      const { previews } = getShowPageScope();
      const preview = previews.find(preview => preview.id === this.selectedInstance.id);
      if (!preview) {
        return;
      }
      const rawObjects = getObjectsForOrder(preview.canvas.getObjects());
      const objects = this.flattenFabricObjects(rawObjects);
      const fields = objects.map(object => {
        if (!object || !object.target) return null;

        let thumbnail = null;        

        if (object.target._element && object.id.startsWith(IMAGE_PREFIX_ID)) {
          const url = new URL(object.target._element.currentSrc);
          if (url.searchParams.size > 0) {
            url.searchParams.set('lightweight', '');
            url.searchParams.set('scale_width', '40');
          }
          thumbnail = url;
        } else if (object.target._element && !object.id.startsWith(IMAGE_PREFIX_ID)) {
          const url = new URL(object.target._element.currentSrc);
          if (url.searchParams.size > 0) {
            url.searchParams.set('lightweight', '');
            url.searchParams.set('is_thumbnail', '1')
          }
          thumbnail = url;
        }
        
        return {
          id: object.target.id || '',
          name: object.name || '',
          type: object.target.templateField?.type || FIELD_TYPE_BACKGROND_THEME_IMAGE,
          thumbnail: thumbnail || DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images,
          visible: object.visible ?? null,
          templateFieldId: object.templateFieldId,
          fileName: object.fileName
        }
      })

      this.fields = fields;
      this.backgroundProperties.selectedField = this.getBackgroundFields
        .find(field => field.name === FIELD_NAME_IMAGE_AREA)?.id || null;
    },
    flattenFabricObjects(objects = []) {
      const result = [];

      objects.forEach(obj => {
        // Group object
        if (obj?.id?.startsWith(KEY_GROUP)) {
          const children = obj.getObjects
            ? obj.getObjects()
            : (obj.objects || obj._objects || []);

          result.push(...this.flattenFabricObjects(children));
        } else {
          result.push(obj);
        }
      });

      return result;
    },
    onImageLoad() {
      this.imagesLoadedCount++
      if (this.imagesLoadedCount >= this.getImages.length) {
        this.loaded = true
      }
    },
    onChangeApplyToAllGlobal() {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, this.isApplyToAllGlobalUpdate);
      if (this.isApplyToAllGlobalUpdate && this.selectedInstance) {
        const GLOBAL_UPDATE = 'globalUpdate'
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, { action:GLOBAL_UPDATE, instance: this.selectedInstance})
      }
    },
    getImagesFiltered() {
      this.loaded = false;
      return this.fields.filter(field => IMG_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible && !field?.name.toLowerCase().includes('background'));
    },
    toggleProductDropdown(item, view = 'replace') {
      if (!item) {
        const activeObjects = this.selectedInstance?.canvas?.getActiveObjects()?.filter(field => field?.id?.startsWith(IMAGE_PREFIX_ID) && field.visible);
        const productImages = this.getProductImages;
        const lastProduct = productImages[productImages.length - 1];

        const lastActiveObject = this.selectedInstance?.canvas
          ?.getObjects()
          .find(obj => obj?.id === lastProduct?.id);

        const activeObject = activeObjects[0] ?? lastActiveObject;
        //open create product images if there is no active object(means all is deleted)
        if (!activeObject) {
          this.selectedInstance.canvas.fire('add:product-images', {
            instanceId: this.selectedInstance.id,
            isNeedAddProductDimensionsField: false,
          })
          return;
        } else {
          this.setActiveObject(activeObject);
        }
      } else {
        this.setActiveObject(item);
      }
      const dropdown = this.$refs.productImageDropdownFields;
      if (!dropdown) return
      setTimeout(() => {
        if (view === 'replace') {
          dropdown.openActiveReplaceMode(!dropdown.isShowDropdown);
        } else if(view === 'replace_all') {
            dropdown.openReplaceAllMode(!dropdown.isShowDropdown, true);
        } else {
          dropdown.onFocusProductIds(!dropdown.isShowDropdown);
        }
      }, 350);
    },
    updateField (field, value, selectedInstance) {
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value,
        action: 'inputText',
        field,
        selectedInstance
      })
    },
    handleClosePopup() {
      this.isShowDropdown = false;
      this.isContracted = false;
    },
    onBlurText() {
      window.isFieldsObject = false;
    },
    setActiveObject (field) {
      this.fileNameIds = field.fileName;
      let target = null;
      if (field.type === FIELD_TYPE_BACKGROND_THEME_IMAGE) {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['id', field.id])
      } else if (field?.id?.startsWith && field.id.startsWith(IMAGE_PREFIX_ID)) {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['id', field.id])
      } else {
        target = _.find(this.selectedInstance.canvas.getObjects(), ['element_id', field.element_id])
      }

      //check if field is part of a group
      if (!target) {
        const groupObject = this.selectedInstance.canvas.getObjects().find(obj => {
          if (obj.id.startsWith(KEY_GROUP)) {
            const children = obj.getObjects
              ? obj.getObjects()
              : (obj.objects || obj._objects || []);
            return children.some(child => 
              (child.id === field.id) || (child.element_id === field.element_id)
            );
          }
          return false;
        });
        if (groupObject) {
          const children = groupObject.getObjects
            ? groupObject.getObjects()
            : (groupObject.objects || groupObject._objects || []);
          target = children.find(child => 
            (child.id === field.id) || (child.element_id === field.element_id)
          );
        }
      }

      window.isFieldsObject = true;
      editorControlEventBroker.fire(EVENTS.SET_ACTIVE_OBJECT, { target })
    },
    shouldShowThisField(field){
      if (!field?.options) {
        return false;
      }
      const options = $.parseJSON(field.options);
      return (options.Option1 !== null && options.Option1 !== "");
    },
    redrawBackground(avoidSettingsUpdate = true) {
      this.editor.redraw()
      this.target.set('fill', new fabric.Pattern({
        source: this.editor.canvas,
        repeat: 'no-repeat'
      }))
      
      if (avoidSettingsUpdate) {
        this.target.updateSettings(deepCopy(this.editor.getSettings()));
        this.target.canvas.requestRenderAll();
      }
    },
    redraw(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        value: '',
        action: 'redrawText',
      })
    },
    moveInstanceSelection(step) {
      const newSelectedInstance = this.currentPagePreviews[this.selectedInstanceIndex + step]
      newSelectedInstance.canvas.wrapperEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setSelectedInstance(newSelectedInstance.id)
    },
    handleProductDelete(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'deleteConfirmation', value: null
      })
    },
    handleImageDelete(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'deleteConfirmationImage', value: null
      })
    },
    // Convert Image to Text methods
    initTextImageClassifications() {
      // First check if we have saved text image classifications
      const savedTextClassifications = window.textImageClassifications;

      if (savedTextClassifications && Object.keys(savedTextClassifications).length > 0) {
        // Get current image IDs to validate classifications
        const currentImageIds = new Set(this.getImages.map(img => img.id));

        // Use saved text image classifications, but only if IDs match current images
        for (const [id, type] of Object.entries(savedTextClassifications)) {
          // Only process if the ID exists in current images (validates bk_theme_img_X format)
          if (currentImageIds.has(id)) {
            this.classifiedImageIds.add(id);
            if (type === 'text') {
              this.textOnlyImageIds.add(id);
            }
          }
        }

        // Mark as classified if we processed any valid classifications
        if (this.classifiedImageIds.size > 0) {
          this.textImagesClassified = true;
        }
        // If savedTextClassifications exists but no IDs matched, hasUnclassifiedImages will be true
        return;
      }

      // Fallback: Check Edit Mode classification for 'Text' type layers
      const savedClassifications = window.layerClassifications;

      if (!savedClassifications || Object.keys(savedClassifications).length === 0) {
        return;
      }

      const { previews } = getShowPageScope();
      const preview = previews.find(p => p.id === this.selectedInstance?.id);
      if (!preview) return;

      // Find images with fieldType 'Text' or 'Multi Line Text' from existing classification
      const images = this.getImages;

      for (const image of images) {
        const obj = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
        if (!obj) continue;

        // Check if this object was classified as text by Edit Mode
        const fieldType = obj.fieldType || obj.templateField?.type;
        if (fieldType === 'Text' || fieldType === 'Multi Line Text') {
          this.textOnlyImageIds.add(image.id);
        }
      }

      // Mark as classified if we found any text images from Edit Mode
      if (this.textOnlyImageIds.size > 0) {
        this.textImagesClassified = true;
      }
    },

    removeConvertedImages() {
      // Remove images that were converted to text (persisted in deleted_converted_images)
      // Also check legacy hidden_converted_images for backwards compatibility
      const deletedImages = this.selectedInstance?.data?.deleted_converted_images || [];
      const hiddenImages = this.selectedInstance?.data?.hidden_converted_images || [];
      const allRemovedImages = [...new Set([...deletedImages, ...hiddenImages])];

      if (allRemovedImages.length === 0) return;

      const { previews } = getShowPageScope();
      const preview = previews.find(p => p.id === this.selectedInstance?.id);
      if (!preview?.canvas) return;

      for (const imageId of allRemovedImages) {
        const obj = this.findCanvasObjectById(preview.canvas.getObjects(), imageId);
        if (obj) {
          preview.canvas.remove(obj);
        }
      }
      preview.canvas.requestRenderAll();
    },

    isTextOnlyImage(image) {
      return this.textOnlyImageIds.has(image.id);
    },

    // Helper to find object by ID recursively (including inside groups)
    findCanvasObjectById(objects, targetId) {
      for (const obj of objects) {
        if (obj.id === targetId) return obj;
        // Search inside groups
        if (obj.type === 'group' && obj.getObjects) {
          const found = this.findCanvasObjectById(obj.getObjects(), targetId);
          if (found) return found;
        }
      }
      return null;
    },

    async handleDetectTextImages() {
      this.isClassifyingTextImages = true;
      try {
        const { previews } = getShowPageScope();
        const preview = previews.find(p => p.id === this.selectedInstance?.id);
        if (!preview) return;

        // Use getImages (same images shown in UI) to collect layers
        const images = this.getImages;
        const layers = [];

        for (const image of images) {
          // Find the canvas object for this image (search recursively in groups)
          const obj = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
          if (!obj) continue;

          // Get image data URL
          const imageUrl = obj.getSrc?.() || obj._element?.src;
          if (!imageUrl) continue;

          try {
            // Convert to base64 if it's a URL
            let imageData = imageUrl;
            if (!imageUrl.startsWith('data:')) {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              imageData = await this.blobToBase64(blob);
            }

            layers.push({
              id: obj.id,
              image_data: imageData,
            });
          } catch (e) {
            console.warn('Could not get image data for', obj.id, e);
          }
        }

        if (layers.length === 0) {
          this.$bvToast.toast('No images found to classify', { variant: 'warning' });
          return;
        }

        // Call backend to classify
        const layoutId = window.layoutId;
        const { data } = await axios.post(`/banner-builder/layouts/${layoutId}/classify-text-images`, { layers });

        if (data.success && data.results) {
          // Update both classifiedImageIds and textOnlyImageIds sets
          // Create new Sets to trigger Vue reactivity
          const newClassifiedIds = new Set(this.classifiedImageIds);
          const newTextOnlyIds = new Set(this.textOnlyImageIds);

          for (const [id, type] of Object.entries(data.results)) {
            newClassifiedIds.add(id);
            if (type === 'text') {
              newTextOnlyIds.add(id);
            }
          }

          // Assign new Sets to trigger reactivity
          this.classifiedImageIds = newClassifiedIds;
          this.textOnlyImageIds = newTextOnlyIds;

          const textCount = this.textOnlyImageIds.size;
          if (textCount > 0) {
            this.$bvToast.toast(`Found ${textCount} text-only image${textCount > 1 ? 's' : ''}`, { variant: 'success' });
          } else {
            this.$bvToast.toast('No text-only images detected', { variant: 'info' });
          }
        }

        this.textImagesClassified = true;
      } catch (error) {
        console.error('Failed to detect text images:', error);
        this.$bvToast.toast('Failed to detect text images', { variant: 'danger' });
      } finally {
        this.isClassifyingTextImages = false;
      }
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    async handleConvertToText(image) {
      const { previews } = getShowPageScope();
      const preview = previews.find(p => p.id === this.selectedInstance?.id);
      if (!preview) return;

      const canvasObject = this.findCanvasObjectById(preview.canvas.getObjects(), image.id);
      if (!canvasObject) return;

      try {
        this.convertingImageId = image.id;

        // 1. Get image URL
        const imageUrl = canvasObject.getSrc?.() || canvasObject._element?.src;
        if (!imageUrl) {
          this.$bvToast.toast('Could not get image source', { variant: 'warning' });
          return;
        }

        // 2. Convert image to base64 for AI analysis
        let imageData = imageUrl;
        if (!imageUrl.startsWith('data:')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          imageData = await this.blobToBase64(blob);
        }

        // 3. Extract text via OCR and style via AI in parallel
        const [extractedText, styleResult] = await Promise.all([
          this.extractTextFromImage(imageUrl),
          axios.post('/banner-builder/extract-text-style', { image_data: imageData })
            .then(res => res.data)
            .catch(() => ({ success: false }))
        ]);

        if (!extractedText?.trim()) {
          this.$bvToast.toast('No text detected in image', { variant: 'warning' });
          return;
        }

        // 4. Calculate position from original image (accounting for origin and scale)
        const { left, top, width, height, scaleX = 1, scaleY = 1, originX = 'left', originY = 'top' } = canvasObject;
        const scaledWidth = width * scaleX;
        const scaledHeight = height * scaleY;

        // Calculate top-left corner based on origin
        let offsetX = left;
        let offsetY = top;
        if (originX === 'center') {
          offsetX = left - (scaledWidth / 2);
        }
        if (originY === 'center') {
          offsetY = top - (scaledHeight / 2);
        }

        // 5. Calculate font size and color from AI analysis
        let textColor = '#000000';
        let fontSize = Math.max(12, scaledHeight * 0.5); // Default fallback

        if (styleResult.success && styleResult.style) {
          textColor = styleResult.style.color || textColor;
          // Calculate font size from percentage of image height
          const fontSizePercent = styleResult.style.fontSizePercent || 10;
          fontSize = Math.max(12, (scaledHeight * fontSizePercent) / 100);
        }

        // 6. Remove original image from canvas and persist the deletion
        preview.canvas.remove(canvasObject);
        preview.canvas.requestRenderAll();

        // Save deleted image ID to layout data so it doesn't reload
        const { updateLayoutChanges } = getShowPageScope();
        const deletedImages = this.selectedInstance.data?.deleted_converted_images || [];
        if (!deletedImages.includes(image.id)) {
          deletedImages.push(image.id);
          updateLayoutChanges(this.selectedInstance.id, 'deleted_converted_images', deletedImages);
        }

        // 7. Add text as a persistent additional field using the canvas event system
        preview.canvas.fire('add:additional-field', {
          instancesIds: [this.selectedInstance.id],
          type: 'Multi Line Text',
          options: {
            text: extractedText,
            _offset_x: offsetX.toFixed(2),
            _offset_y: offsetY.toFixed(2),
            _color: textColor,
            _fontsize: Math.round(fontSize).toString(),
            _width: scaledWidth.toFixed(2),
            _height: scaledHeight.toFixed(2),
            name: 'Converted Text',
          },
        });

        // Remove from text-only set since it's converted
        this.textOnlyImageIds.delete(image.id);

        this.$bvToast.toast('Converted to editable text', { variant: 'success' });
      } catch (error) {
        console.error('Convert to text failed:', error);
        this.$bvToast.toast('Failed to convert image to text', { variant: 'danger' });
      } finally {
        this.convertingImageId = null;
      }
    },

    async extractTextFromImage(imageUrl) {
      try {
        // Convert URL to blob/file for OCR
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'image.png', { type: blob.type });

        // Call OCR endpoint
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', 'ARS'); // Amazon Rekognition

        const { data } = await axios.post('/ocr/recognize-image-text', formData);

        // Join text lines
        return Array.isArray(data) ? data.join('\n') : data;
      } catch (error) {
        console.error('OCR failed:', error);
        return null;
      }
    },

    extractDominantColor(canvasObject) {
      try {
        // Try to get dominant color from image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = canvasObject._element;

        if (!img) return null;

        canvas.width = 1;
        canvas.height = 1;
        ctx.drawImage(img, 0, 0, 1, 1);

        const pixel = ctx.getImageData(0, 0, 1, 1).data;
        return `rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`;
      } catch (e) {
        return null;
      }
    },

    handleDeleteText(field) {
      this.setActiveObject(field);
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        action: 'delete', value: null
      })
    },
    getMaxCharsValue(field) {
      let maxCharsFromBaseField = 10000;
      try {
        const options = JSON.parse(field.options)
        maxCharsFromBaseField = options['Max Chars']
      } catch (e) {
        //do nothing
      }
      return Number(this.selectedInstance.data[`${field.element_id}_max_chars`] || maxCharsFromBaseField)
    },
    toggleLoading({ loadingType }) {
      this[loadingType] = !this[loadingType];
    },
    getValidatedValue(value) {
      return typeof value === 'boolean' ? '' : (value || '');
    },
    onChange(action, value, file = null) {
      if (action === 'selectBackground') {
        if (!this.selectedBackground) {
          toastr.error("Please select a Background Image first.");
          return;
        }
        window.setAsActiveReplacedImage = false;
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
          action, value, background_id: this.selectedBackground.id, file
        })

        return;
      }
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    onTextChange (action, value) {
      if (!Array.isArray(this.selected)) {
        const oldValue = this.currentTarget.text;
        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
          action,
          value,
          maximizeFontSize: this.shouldMaximizeFontSize,
          keepLineBreaks: this.keepLineBreaks,
          useTemplateFontSize: this.useTemplateFontSize,
          oldValue
        })
      } else {
        editorControlEventBroker.fire(EVENTS.CUSTOM_GROUP_CHANGED, {
          action,
          value,
        });

        if (action === 'textSpacing' || action === 'fontSize') {
          // update selection box after new changes
          const selectedObjects = this.selectedInstance?.canvas?.getActiveObjects();
          selectedObjects.forEach(obj => {
                if (obj.setCoords) {
                  obj.setCoords();
                }
          });
          this.selectedInstance?.canvas.discardActiveObject();
          const selection = new fabric.ActiveSelection(selectedObjects, {
            canvas: this.selectedInstance?.canvas
          })

          selection.setCoords();
          this.selectedInstance?.canvas.setActiveObject(selection)
          this.selectedInstance?.canvas.requestRenderAll()
          this.$store.commit('layout/SET_TARGET', selection);
        }
      }
    },
    addAdditionalField(type, file = null) {
      document.querySelector('.back-to-folder-link').click();

      const existingElementIds = new Set();
      const existingIds = new Set();
      
      const currentObjects = this.selectedInstance.canvas.getObjects();
      currentObjects.forEach((obj, index) => {
        if (obj.element_id) existingElementIds.add(obj.element_id);
        if (obj.id) existingIds.add(obj.id);
      });

      this.selectedInstance.canvas.once('object:added', (e) => {
        const file = e.target?._file || null;
        setTimeout(() => {
          const updatedObjects = this.selectedInstance.canvas.getObjects();
          
          let newObject = null;

          updatedObjects.forEach((obj, index) => {
            if (obj.element_id && !existingElementIds.has(obj.element_id)) {
              newObject = obj;
            }
            else if (obj.id && !existingIds.has(obj.id)) {
              if (!newObject) newObject = obj;
            }
          });

          if (!newObject && updatedObjects.length > currentObjects.length) {
            newObject = updatedObjects[updatedObjects.length - 1];
          }

          if (newObject) {
            this.selectedInstance.canvas.setActiveObject(newObject);
            this.selectedInstance.canvas.renderAll();

            editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
              action: 'selectBackground', file
            });
          }
        }, 50);
      });

      this.selectedInstance.canvas.fire('add:additional-field', {
        instancesIds: [this.selectedInstance.id],
        type,
        options: {file}
      })
    },
    replaceImage(field, action, value, file = null)
    {
      this.setActiveObject(field);

      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value, file
      })
    },
    replaceAllImage(action, value)
    {
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects()?.filter(field => IMG_PREFIXES.some(prefix => field?.id.startsWith(prefix)) && field.visible && !field?.templateField?.name?.toLowerCase().includes('background'));
      const field = this.getImages[this.getImages.length - 1]; //if there are no active objects in canvas select the last image
      this.setActiveObject(activeObjects[0] ?? field);

      window.fieldsReplaceAllImage = true;

      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    onInputColor(inputColor, index) {
      if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(inputColor)) {
        this.$set(this.editor.colors, index, inputColor);
        this.redrawBackground();
      } else {
        toastr.error("Invalid color format. Please use hex color codes.");
      }
    },
    
    onDragEnterProductImages(e) {
      this.isDragOverProductImages = true;
    },
    onDragOverProductImages(e) {
      this.isDragOverProductImages = true;
    },
    onDragLeaveProductImages(e) {
      // Only set to false if leaving the dropzone completely
      if (!e.currentTarget?.contains(e.relatedTarget)) {
        this.isDragOverProductImages = false;
      }
    },
    async onDropProductImage(e) {
      this.isDragOverProductImages = false;

      const files = e.dataTransfer.files;

      // Validation: Max 1 file
      if (files.length === 0) {
        toastr.error('No file selected');
        return;
      }

      if (files.length > 1) {
        toastr.error('Please upload only 1 file at a time');
        return;
      }

      const file = files[0];

      if (!validateProductImage(file)) {
        return;
      }

      await uploadProductImageAndDraw({
        file,
        canvas: this.selectedInstance.canvas,
        selectedLayoutId: this.selectedInstance.id,
        existingImages: this.getProductImages
      });
    },
    async onDropReplaceProductImage(e, image) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.setActiveObject(image);
          const dragDropService = new TemplateDragDropService(window.previews, window.template_data);
          await dragDropService.handleProductImageDrop({
            file,
            targetPreview: this.selectedInstance,
            replaceProductImage: image
          });
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    onDropAddImage(e) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.addAdditionalField('Background Theme Image', file)
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    onDropReplaceLayoutImage(e, field) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.replaceImage(field, 'selectBackground', 'Replace', file)
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    onDragEnter(e) {
      this.isDragOver = true;
    },

    onDragOver(e) {
      this.isDragOver = true;
    },

    onDrop(e) {
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        // Validate file type
        if (file.type.startsWith('image/')) {
          this.handleFile(file);
        } else {
          toastr.error('Please upload an image file');
        }
      }
    },
    handleFile(file) {
      this.onChange('selectBackground', 'Replace', file);
    },
    async confirmApply() {
      
      const selectedTemplates = localStorage.getItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT
      );

      // set seleted templates for BG_COLOR
      localStorage.setItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
        selectedTemplates
      );

      localStorage.setItem(
        LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE,
        selectedTemplates
      );

      const { previews } = getShowPageScope();
      const preview = previews.find(preview => preview.id === this.selectedInstance.id);
      if (!preview) {
        return;
      }
      const objects = preview.canvas.getObjects().filter(object => object.visible === true && object.element_id?.includes(TEXT_PREFIX_ID));
      if (Array.isArray(objects) && objects.length > 0) {
        const selection = new fabric.ActiveSelection(objects, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();

        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
          action: 'text',
          value: true,
          keys: [],
        });
      }

      //if no background color is set skip
      if (this.editor?.colors[0] !== this.defaultColor) { //#fff is default color on mounted
        editorControlEventBroker.fire(EVENTS.BG_COLOR_CHANGED, [{
          text: 'Update Color',
          key: 'update_color',
          value: false,
          instance_id: this.target.canvas.instance_id,
          backgroundElementId: this.target.id,
          objectSearch: 'fieldType'
        }]);
      }

      //if product images are empty skip apply
      if (this.getProductImages.length > 0) {
          await this.selectedInstance.canvas.fire('product-images-selector:apply-to', this.selectedInstance.id);
      }
 
      // setSelectedInstance(testInstance.id)
      //if background image is empty skip
      if (this.selectedBackground) {
        const selectObject = preview.canvas.getObjects().filter(object => object.id === this.selectedBackground.id);
        const selection = new fabric.ActiveSelection(selectObject, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();
        await editorControlEventBroker.fire(EVENTS.CHANGE_IMAGE_PROPERTIES, [{
          text: 'Replace images',
          key: 'replace',
          value: false
        }]);
      }

      if (this.getImages.length > 0) {
        //get only the images
        const selectObject = preview.canvas.getObjects().filter(object => this.getImages.some(image => image.id === object.id));
        const selection = new fabric.ActiveSelection(selectObject, { canvas: preview.canvas });
        preview.canvas.setActiveObject(selection);
        preview.canvas.requestRenderAll();
        await editorControlEventBroker.fire(EVENTS.CHANGE_IMAGE_PROPERTIES, [{
          text: 'Replace images',
          key: 'replace',
          value: false
        }]);
      }
    },
    preventNegativeInput(event) {
      if (event.key === '-' || event.key === 'e') {
        event.preventDefault();
      }
    },
    decrease() {
      this.onTextChange('textSpacing', Number(this.currentTarget.charSpacing ?? this.currentTarget?.getObjects?.()[0]?.charSpacing) - 10);
    },
    increase() {
      this.onTextChange('textSpacing', Number(this.currentTarget.charSpacing ?? this.currentTarget?.getObjects?.()[0]?.charSpacing) + 10);
    },
    addNewTextField() {      
      this.handleFieldAddedAndShowSettings(this.selectedInstance.canvas);
      
      this.selectedInstance.canvas.fire('add:additional-field', {
        instancesIds: [this.selectedInstance.id],
        type: 'Multi Line Text'
      })
    },
    handleFieldAddedAndShowSettings(canvas) {
      canvas.once('object:added', (e) => {
        const newObject = e.target;
        if (newObject) {
          canvas.setActiveObject(newObject);
          canvas.renderAll();
        }
      });
    },
    handleSectionsState() {
      if (this.typeEditorControl === 'TEXT' && this.isOpen.textEditorFieldsSection === false) {
        this.$emit('toggleSection', 'textEditorFieldsSection');
      } else if (this.typeEditorControl === 'IMAGE') {
        if (this.activeTarget?.id?.startsWith(IMAGE_PREFIX_ID) && this.isOpen.productImagesFieldsSection === false) {
          this.$emit('toggleSection', 'productImagesFieldsSection');
          return;
        } else if (IMG_PREFIXES.some(prefix => this.activeTarget?.id.startsWith(prefix)) && this.isOpen.imagesFieldSection === false) {
          this.$emit('toggleSection', 'imagesFieldSection');
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.background-dropdown-label,
.background-dropdown-option {
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.background-dropdown-label__image,
.background-dropdown-option__image {
  position: relative;
  width: 100%;
  flex-shrink: 0;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.background-dropdown-label__image {
  max-width: 50px;
  padding-top: 50px;
}

.background-dropdown-option__image {
  max-width: 30px;
  padding-top: 30px;
}

.background-dropdown-option__text {
  margin: 0;
}

.button-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  button {
    white-space: nowrap;
  }

  .maintain-button-width {
    min-width: max-content;
  }
  .colors-item {
    width: 100%;
    padding-bottom: 10px;
  }
  .colors-item input {
    // background-color: #f8f9fa !important;
    border-radius: 0.35rem !important;
    border-color: #e9ecef !important;
  }
  .multiselect {
    min-width: 80px;
  }
  .background-dropdown {
    .background-dropdown {
        margin-top: 10px;
      }
      .background-dropdown-label {
        width: 60px;
        height: 22px;
      }

      ::v-deep(.multiselect__select) {
        left: 50px !important;
        right: auto;
      }

      ::v-deep(.multiselect__single) {
        padding-left: 0 !important;
      }
  }
  
::v-deep(.background-dropdown .multiselect__content-wrapper) {
  width: 245px;
  box-sizing: border-box;
  left: -5px;
}

.multiselect__single .background-dropdown-label__image{
  max-width: 28px !important;
  padding-top: 35px !important;
}

.preview-img:hover .delete-overlay {
    display: block;
}
.cloud-overlay-icon {
  position: absolute;
  z-index: 10;
  top: 0;
  left: -15px;
  cursor: pointer;
}
.background-dropdown-label__image {
  margin-left: 2px;
}
.label-option {
  position: relative;
  margin: 0 5px;
}
.image-option {
  width: 30px;
  height: auto;
}
.label-option .close-btn {
  opacity: 0; 
  position: absolute;
  top: 1px;
  right: 2px;
  background-color: red;
  vertical-align: -.15em;
  color: white;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  transition: opacity 0.3s ease-in-out;
}

.replace-image-btn i {
  color: #000;
}

.label-option .replace-image-btn {
  position: absolute;
  bottom: 0;
  left: 2px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border: 0;
  background: transparent;
  padding: 0;
  color: #209c86;
}

.label-option:hover .close-btn,
.label-option:hover .replace-image-btn,
.label-option:hover .convert-text-btn {
  opacity: 1;
}

.label-option .convert-text-btn {
  position: absolute;
  bottom: 0;
  right: 2px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border: 0;
  background: transparent;
  padding: 0;
  color: #007bff;
}

.convert-text-btn i {
  color: #007bff;
}

.convert-text-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.detect-text-btn {
  font-size: 0.75rem;
  padding: 0.2rem 0.4rem;
}

.dropdown-product-images {
  & input:hover { 
    cursor: pointer;
  }
}

.product-ids-input[readonly] {
    background: #fff;
}

.file-browser-wrapper {
    max-height: 770px;
    overflow-y: auto;
    ::v-deep {
        .c-main-view {
            border: none;
        }
    }
}

.buttons-wrapper {
  display: flex;
  gap: 10px;
  margin: 15px 10px 0 0;
  justify-content: end;
}
::v-deep {
  .products-container {
    width: calc(65vw - 9px);
    height: fit-content;
    position: fixed;
    background: rgb(255, 255, 255);
    z-index: 101;
    top: 87px;
    right: 64px;
    border: 1px solid;
    box-shadow: 2px 2px #888888;
    overflow-y: auto;
    padding: 20px;
  }
  
  .contract-btn-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .contract-btn, .close-btn {
    cursor: pointer;
  }

  .products-container--contracted {
    width: 400px;
    padding: 5px 20px;
    
    .file-browser-wrapper {
      display: none;
    }
    
    .buttons-wrapper {
      display: none;
    }
  }
}

.replace-options-dropdown {
  .list-group {
    min-width: 300px;
    .list-group-item {
      &:first-child {
        border-top: 0;
      }
      &:last-child {
        border-bottom: 0;
      }
    }
    
    
  }
  .dropdown-menu.show {
    padding-top:0 !important;
    padding-bottom: 0 !important;
    border:0 !important;
  }
}

.add-icon {
  cursor: pointer;
}

.color-fill-item {
  height: 38px !important;
  padding: 7px 7px !important;
  width: 15% !important;
}

.color-fill-item:hover {
  cursor: pointer;
}

.color-fill-item:focus {
  box-shadow: none !important;
  outline: none !important;
  border-color: #ced4da !important;
}

::v-deep .multiselect__tags {
  min-height: 40px !important;
  border: 0 !important;
}

@media screen and (max-height: 730px) {
  .file-browser-wrapper {
    max-height: 400px;
  }
  
  .products-container {
    height: 500px;
  }

  .products-container--contracted {
    height: auto;
    width: 400px;
    padding: 5px 20px;
  }
  
}
.banner-module strong {
  font-size: 0.875rem !important;
  letter-spacing: 0.3px;
}
.green-fonts {
  color: #209c86 !important;
  margin-right: 5px;
}
.upload-button {
  border-radius: 0.35rem !important;
  border: 1px solid #e9ecef;
  padding-right: 0;
  padding-left: 10px;
  background-color: #209c86 !important;
  color: white;
  box-shadow: none !important;
}

.upload-button:hover {
  background-color: #209c86 !important;
}

.upload-button i {
  filter: drop-shadow(0 0 0 currentColor);
  margin-bottom: 12px !important;
}
.general-card-template {
  display: inline-block;
  margin: 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  background: #f4f4f4;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.image-card-template {
  width: 90px;
  height: 90px;
}
.image-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.additional-card-template {
  margin: 8px;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  width: 90px;
  height: 90px;
}
.product-card-template {
  width: 65px;
  height: 65px;
}
.product-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.image-loader {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
img.is-loading {
  filter: blur(6px);
  opacity: 0.5;
}
.scroll-images-section{
  width: 100%;
  max-height: 222px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  scrollbar-width: thin;
}
.colors-item  .no-right-border {
  border-right: none !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.colors-item  .no-left-border {
  border-left: none !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.image-multiselect{
  border: 1px solid #e9ecef !important;
  padding: 0;
  background: white;
  border-radius: 6px;
}

.custom-color-picker {
  width: 42px !important;      
  height: 38px !important;   
  border-radius: 8px; 
  overflow: hidden;
  padding: 0;
  cursor: pointer;
}

.custom-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.custom-color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px; 
}

.custom-color-picker::-moz-color-swatch {
  border: none;
  border-radius: 4px;
}

.color-fill-item:focus {
  border-color: none !important;
}

.custom-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  color: #777;
}
::v-deep .btn-group .btn {
    border-radius: 0.2rem !important;
}
::v-deep .color-checkbox-container .pickr button {
  width: 100% !important;
  height: 30px !important;
}
::v-deep .color-checkbox-container {
  flex-direction: column !important;
  align-items: flex-start !important;
}
::v-deep .color-checkbox-container .pickr {
  width: 100% !important;
}
::v-deep .color-checkbox-container .picker-container {
  width: 100% !important;
  margin-top: 10px;
}
.sticky-dropdown {
  position: fixed;
  bottom: 0;
  background-color: white;
  padding: .75rem .5rem;
  width: 100%;
  z-index: 1;
}
.pointer {
  cursor: pointer;
}

.layout-sidebar-section {
  margin-bottom: 1rem;

  .collapse {
    padding: 0 0.75rem;
  }
}

.layout-sidebar-section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  border-radius: 0.5rem;
  background-color: hsla(210, 40%, 96.1%, 0.5);

  &:hover {
    background-color: hsl(210, 40%, 96.1%);
  }
}
.custom-gen {
  background: linear-gradient(
    90deg,
    rgb(130, 59, 234) 0%,
    rgb(95, 74, 234) 40%,
    rgb(56, 91, 235) 100%
  );
  border: none;
  width: 100%;
}
.custom-secondary-btn {
  border: 1px solid #e2e8f0;
  background-color: #fff;
  border-radius: 6px;
}
.custom-secondary-btn:hover {
  color: #fff;
  background-color: #209c86;
}
.selected {
  border: 3px solid #059669;
}

/* Edit mode restriction styles */
.edit-restricted {
  opacity: 0.6;
  cursor: not-allowed;

  .image-option {
    cursor: not-allowed;
  }
}

.locked-overlay {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
